import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  getRateCards,
  getMaterials,
  getProductTypes,
  createLaborCard,
  updateLaborCard,
  deleteLaborCard,
  createFinishRate,
  updateFinishRate,
} from "../../services/apiService";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import {
  Plus,
  ChevronDown,
  ChevronRight,
  X,
  Database,
  Table2,
  Check,
  AlertCircle,
} from "lucide-react";
import RateCardPreviewRail from "./RateCardPreviewRail";
import RateCardDeriveStrip from "./RateCardDeriveStrip";
import HistoricalSearchDialog from "./HistoricalSearchDialog";
import CostedJobReferences from "./CostedJobReferences";
import CostedJobReferenceDetail from "./CostedJobReferenceDetail";
import {
  countWithActuals,
  isVerifiedCushion,
  formatCushion,
  medianCushion,
} from "./costedJobReference";
import WorkbenchShell from "../workbench/WorkbenchShell";
import { StatusCell } from "../workbench/WorkbenchStatusBar";
import "./adminShell.css";
import "./RateCardBuilderPage.css";
import {
  PRICED_BY_OPTIONS,
  LEGACY_BASIS_LABELS,
  STATUS_OPTIONS,
  STATUS_DESCRIPTIONS,
  BAND_BY_CHIPS,
  BAND_BY_HINTS,
  unitForBasis,
  inferGeometryMethod,
  sqftSourceLabel,
  formatMoney,
  bandLabel,
  bandDimLabel,
} from "./rateCardConstants";
import {
  APPROVAL_STATES,
  rollUpApproval,
  isPendingApproval,
  pendingStatusOf,
  summarizeApprovalQueue,
  approvalQueueHeadline,
} from "./rateCardApproval";

const LABOR_DEPTS = ["cnc", "cut", "fab", "paint"];
const RATE_FIELDS = LABOR_DEPTS.map((d) => `${d}_per_sqft`);
const DEFAULT_BAND_LABELS = ["Small", "Medium", "Large"];

const toInput = (value) =>
  value === null || value === undefined ? "" : String(value);

/* The live price preview is opt-in on this screen: the builder is a place for
 * setting rates, and the price only matters once you look for it. Collapsed by
 * default, remembered per browser — the same localStorage convention the
 * Workbench uses for its own panes (see WorkbenchContext). */
const PREVIEW_STORAGE_KEY = "costpilot.wb.rateCardPreviewOpen";

function readPreviewFlag() {
  try {
    return window.localStorage.getItem(PREVIEW_STORAGE_KEY) === "true";
  } catch (error) {
    return false;
  }
}

function writePreviewFlag(value) {
  try {
    window.localStorage.setItem(PREVIEW_STORAGE_KEY, value ? "true" : "false");
  } catch (error) {
    /* storage unavailable — the toggle still works for this session */
  }
}

/** Status-strip money. The value is whatever the preview endpoint returned. */
const previewMoney = (value) =>
  value === null || value === undefined ? "—" : formatMoney(value);
const numOrNull = (v) =>
  v === "" || v === null || v === undefined ? null : Number(v);

function defaultBandLabelFor(index) {
  return DEFAULT_BAND_LABELS[index] || `Band ${index + 1}`;
}

/** Column-width bands are the column norm; else band by width. */
function inferBandDim(category) {
  return (category || "").toLowerCase().includes("column")
    ? "cross_max"
    : "width";
}

function blankForm() {
  return {
    id: null,
    category: "",
    material: "",
    default_material_thickness_in: "",
    default_finish_rate_id: "",
    notes: "",
    labor_basis: "sqft",
    geometry_method: "flat_2d",
    cnc_per_sqft: "",
    cut_per_sqft: "",
    fab_per_sqft: "",
    paint_per_sqft: "",
    min_labor_hours: "",
    active: true,
    status: "devised",
    source: "",
  };
}

function cardToForm(card) {
  return {
    id: card.id,
    category: toInput(card.category),
    material: toInput(card.material),
    default_material_thickness_in: toInput(card.default_material_thickness_in),
    default_finish_rate_id: toInput(card.default_finish_rate_id),
    notes: toInput(card.notes),
    labor_basis: card.labor_basis || "sqft",
    geometry_method: card.geometry_method || "flat_2d",
    cnc_per_sqft: toInput(card.cnc_per_sqft),
    cut_per_sqft: toInput(card.cut_per_sqft),
    fab_per_sqft: toInput(card.fab_per_sqft),
    paint_per_sqft: toInput(card.paint_per_sqft),
    min_labor_hours: toInput(card.min_labor_hours),
    active: card.active !== false,
    status: card.status || "devised",
    // Read-only here: the API stamps it whenever a rate actually moves.
    rate_confirmed_at: card.rate_confirmed_at || null,
    source: toInput(card.source),
  };
}

/** One band row (a sibling card) from a stored card. */
function cardToRow(card) {
  return {
    id: card.id,
    band_label: toInput(card.band_label),
    size_band_min_in: toInput(card.size_band_min_in),
    size_band_max_in: toInput(card.size_band_max_in),
    cnc_per_sqft: toInput(card.cnc_per_sqft),
    cut_per_sqft: toInput(card.cut_per_sqft),
    fab_per_sqft: toInput(card.fab_per_sqft),
    paint_per_sqft: toInput(card.paint_per_sqft),
  };
}

const byBandMin = (a, b) =>
  (numOrNull(a.size_band_min_in) ?? 0) - (numOrNull(b.size_band_min_in) ?? 0);

/** Human string for a card in confirm dialogs. */
function describeCard(card) {
  const label = card.band_label || bandLabel(card) || "band";
  const range = bandLabel(card);
  return range && range !== label ? `${label} (${range})` : label;
}

/** CSS modifier suffix per roll-up state; the colours live in the stylesheet. */
/** Short basis wording for a rail row's detail line. */
const LABOR_BASIS_SHORT = {
  lineal_ft: "lin-ft",
  face: "face",
  unit: "unit",
  sqft: "sqft",
};

const APPROVAL_TONE = {
  [APPROVAL_STATES.APPROVED]: "approved",
  [APPROVAL_STATES.NEEDS_APPROVAL]: "needs-approval",
  [APPROVAL_STATES.CHECK_RATES]: "check-rates",
  [APPROVAL_STATES.NEEDS_RATES]: "needs-rates",
};

/**
 * Roll-up badge — the ONE status element on a rail row. It always carries its
 * word, so colour is never the only signal (and the row no longer repeats that
 * status as a dot and a sub-label as well).
 */
function ApprovalBadge({ rollup, className = "" }) {
  if (!rollup) return null;
  const tone = APPROVAL_TONE[rollup.state] || "needs-approval";
  return (
    <span
      className={`rc-badge rc-badge--${tone} ${className}`}
      title={rollup.hint}
    >
      {rollup.label}
    </span>
  );
}

/** Row class for a flat rail row, selected or not.
 *
 * `wb-plain` carries no rules of its own — the global button chrome in
 * index.css/App.css applies to every `button:not([class*="wb-"])`, and a
 * class containing "wb-" is the codebase's documented opt-out. Without it
 * the rows inherit an 8px margin and hover-lift and overflow the rail
 * under the scrollbar. */
function rowClass(isActive) {
  return `wb-plain rc-row${isActive ? " rc-row--on" : ""}`;
}

/** Line 2 of a template row: how much of this template is actually priced. */
function coverageLabel(row) {
  if (!row.covered) return "No rates yet";
  return row.bandCount > 0 ? `${row.bandCount} bands · priced` : "Priced";
}

/** "March 3, 2026" for the approval banner; null when nothing was stamped. */
function formatApprovedOn(value) {
  if (!value) return null;
  const when = new Date(value);
  if (Number.isNaN(when.getTime())) return null;
  return when.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

/** Soft contiguity warnings across band rows (never blocks save). */
function computeBandWarnings(bands) {
  const warnings = [];
  for (let i = 0; i < bands.length - 1; i += 1) {
    const cur = bands[i];
    const nxt = bands[i + 1];
    const curLabel = cur.band_label || defaultBandLabelFor(i);
    const nxtLabel = nxt.band_label || defaultBandLabelFor(i + 1);
    const curMax = numOrNull(cur.size_band_max_in);
    const nxtMin = numOrNull(nxt.size_band_min_in);
    if (curMax === null) {
      warnings.push(
        `“${curLabel}” has no upper limit but isn’t the last band — later bands won’t be reached.`,
      );
      continue;
    }
    if (nxtMin === null) continue;
    if (nxtMin < curMax) {
      warnings.push(
        `“${curLabel}” and “${nxtLabel}” overlap around ${curMax}″.`,
      );
    } else if (nxtMin > curMax) {
      warnings.push(
        `Gap between “${curLabel}” (ends ${curMax}″) and “${nxtLabel}” (starts ${nxtMin}″).`,
      );
    }
  }
  return warnings;
}

function RateCardBuilderPage() {
  const [laborCards, setLaborCards] = useState([]);
  const [finishRates, setFinishRates] = useState([]);
  const [materials, setMaterials] = useState([]);
  // Registry of product types ("templates"). Every active type gets a rail row
  // whether or not it has rates yet, so the owner sees the full fill-in list.
  const [productTypes, setProductTypes] = useState([]);
  const [showHistorical, setShowHistorical] = useState(false);
  // The `pricing` block the preview endpoint returned for the Typical
  // scenario, mirrored into the status strip. Never derived here.
  const [previewPricing, setPreviewPricing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  // Costed Job References — evidence that lives INSIDE the build, as a
  // collapsible section beside the department-hours fields. It never replaces
  // the card form; the only pane it borrows is the inspector, and only once a
  // row is picked.
  const [referencesExpanded, setReferencesExpanded] = useState(false);
  const [referenceScope, setReferenceScope] = useState("est");
  const [referenceResults, setReferenceResults] = useState([]);
  const [selectedReference, setSelectedReference] = useState(null);
  // The inspector shows the live price preview, or — once a costed line is
  // picked — that line's detail. Swappable either way from the pane header.
  const [inspectorView, setInspectorView] = useState("preview");
  // The costed line the owner pulled rates from, handed to the derive strip as
  // { item, pullId } so re-pulling the same row is a distinct event.
  const [pulledReference, setPulledReference] = useState(null);
  const pullSeq = useRef(0);
  // Inspector pane: collapsed by default, remembered across visits.
  const [inspectorOpen, setInspectorOpen] = useState(readPreviewFlag);

  const [form, setForm] = useState(blankForm);
  const [search, setSearch] = useState("");
  // Approval-queue filter: show only the rows waiting on the owner.
  const [onlyPending, setOnlyPending] = useState(false);

  // Banded family editing state
  const [banded, setBanded] = useState(false);
  const [bandDim, setBandDim] = useState("");
  const [bands, setBands] = useState([]);
  // Original cards that were loaded as the current family (for save/diff).
  const [loadedFamily, setLoadedFamily] = useState([]);

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(null);

  const loadAll = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const [rateCardData, materialData, productTypeData] = await Promise.all([
        getRateCards(),
        getMaterials(),
        // Registry fetch is best-effort: if it fails, the rail falls back to
        // the legacy category groups below (fail soft, never blocks the page).
        getProductTypes().catch((err) => {
          console.warn("Failed to load product types (registry):", err);
          return null;
        }),
      ]);
      setLaborCards(rateCardData?.labor_cards || []);
      setFinishRates(rateCardData?.finish_rates || []);
      setMaterials(materialData || []);
      setProductTypes(productTypeData?.product_types || []);
      return rateCardData;
    } catch (err) {
      setLoadError(err.message || "Failed to load rate cards");
      return null;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  const setField = (field, value) => {
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      if (field === "category" && !prev.id) {
        next.geometry_method = inferGeometryMethod(value);
      }
      return next;
    });
    setSaveSuccess(null);
  };

  /**
   * Everything that belonged to the card we are leaving. Called by every
   * card-selection handler below.
   *
   * The picked costed line goes with it: a reference selected against the old
   * card must not linger in the inspector, and a pulled line must not be
   * sitting in the pull-rates strip ready to prefill a card it was never
   * evidence for. The inspector returns to the live preview.
   */
  const resetSelectionMessages = () => {
    setSaveError(null);
    setSaveSuccess(null);
    setSelectedReference(null);
    setPulledReference(null);
    setInspectorView("preview");
  };

  const handleNewCard = () => {
    setForm(blankForm());
    setBanded(false);
    setBandDim("");
    setBands([]);
    setLoadedFamily([]);
    setReferencesExpanded(false);
    resetSelectionMessages();
  };

  // Load a single, unbanded card.
  const handleSelectCard = (card) => {
    setForm(cardToForm(card));
    setBanded(false);
    setBandDim("");
    setBands([]);
    setLoadedFamily([card]);
    setReferencesExpanded(false);
    resetSelectionMessages();
  };

  // Load a whole banded family (all cards sharing category + size_band_dim).
  const handleSelectFamily = (category, dim) => {
    const family = laborCards
      .filter((c) => c.category === category && c.size_band_dim === dim)
      .sort(byBandMin);
    if (family.length === 0) return;
    setForm(cardToForm(family[0]));
    setBanded(true);
    setBandDim(dim);
    setBands(family.map(cardToRow));
    setLoadedFamily(family);
    setReferencesExpanded(false);
    resetSelectionMessages();
  };

  // Start a fresh card with the category pre-filled (from a template click).
  // Mirrors handleNewCard + the category → geometry inference in setField, and
  // seeds the band dim the same way handleSetBanded would if toggled banded.
  const handleNewCardForCategory = (category) => {
    setForm({
      ...blankForm(),
      category,
      geometry_method: inferGeometryMethod(category),
    });
    setBanded(false);
    setBandDim(inferBandDim(category));
    setBands([]);
    setLoadedFamily([]);
    setReferencesExpanded(false);
    resetSelectionMessages();
  };

  // Click a product template: open its rates if it has any, else start a new
  // card preset to that type's key so the owner just fills in the blanks.
  const handleSelectTemplate = (row) => {
    if (row.primaryFamily) {
      handleSelectFamily(row.type.key, row.primaryFamily.dim);
    } else if (row.singles.length) {
      handleSelectCard(row.singles[0]);
    } else {
      handleNewCardForCategory(row.type.key);
    }
  };

  // Toggle between one-rate and banded editing.
  const handleSetBanded = (nextBanded) => {
    setSaveSuccess(null);
    if (nextBanded === banded) return;
    if (nextBanded) {
      const dim = bandDim || inferBandDim(form.category);
      const firstRow = {
        id: form.id || null,
        band_label: "Small",
        size_band_min_in: "0",
        size_band_max_in: "",
        cnc_per_sqft: form.cnc_per_sqft,
        cut_per_sqft: form.cut_per_sqft,
        fab_per_sqft: form.fab_per_sqft,
        paint_per_sqft: form.paint_per_sqft,
      };
      setBandDim(dim);
      setBands([firstRow]);
      setBanded(true);
    } else {
      // Un-band: keep the first band's rates on a single card.
      const first = bands[0] || {};
      setForm((prev) => ({
        ...prev,
        id: first.id || null,
        cnc_per_sqft: first.cnc_per_sqft ?? prev.cnc_per_sqft,
        cut_per_sqft: first.cut_per_sqft ?? prev.cut_per_sqft,
        fab_per_sqft: first.fab_per_sqft ?? prev.fab_per_sqft,
        paint_per_sqft: first.paint_per_sqft ?? prev.paint_per_sqft,
      }));
      setBanded(false);
    }
  };

  const setBandRow = (index, field, value) => {
    setSaveSuccess(null);
    setBands((prev) => {
      const next = prev.map((r, i) =>
        i === index ? { ...r, [field]: value } : r,
      );
      // Contiguity nicety: pushing a row's "To" carries the next row's "From"
      // along if it still matched the old boundary.
      if (field === "size_band_max_in" && index < next.length - 1) {
        const oldTo = prev[index].size_band_max_in;
        if (next[index + 1].size_band_min_in === oldTo) {
          next[index + 1] = { ...next[index + 1], size_band_min_in: value };
        }
      }
      return next;
    });
  };

  const addBand = () => {
    setSaveSuccess(null);
    setBands((prev) => {
      const last = prev[prev.length - 1];
      return [
        ...prev,
        {
          id: null,
          band_label: defaultBandLabelFor(prev.length),
          size_band_min_in: last ? last.size_band_max_in : "",
          size_band_max_in: "",
          cnc_per_sqft: "",
          cut_per_sqft: "",
          fab_per_sqft: "",
          paint_per_sqft: "",
        },
      ];
    });
  };

  const removeBand = (index) => {
    setSaveSuccess(null);
    setBands((prev) =>
      prev.length <= 1 ? prev : prev.filter((_, i) => i !== index),
    );
  };

  const applyDerivedRates = (derivedRates) => {
    setForm((prev) => ({
      ...prev,
      cnc_per_sqft: toInput(derivedRates.cnc),
      cut_per_sqft: toInput(derivedRates.cut),
      fab_per_sqft: toInput(derivedRates.fab),
      paint_per_sqft: toInput(derivedRates.paint),
    }));
    setSaveSuccess(null);
  };

  const toggleInspector = () =>
    setInspectorOpen((open) => {
      writePreviewFlag(!open);
      return !open;
    });

  /* ── Costed Job References ── */

  /** Expand the section and scroll-free-ly bring its evidence into play. */
  const toggleReferences = () => setReferencesExpanded((open) => !open);

  /**
   * Picking a row shows that line in the inspector — the one pane the section
   * borrows, and only because a per-department comparison needs the room.
   */
  const handleSelectReference = (item) => {
    setSelectedReference(item);
    setInspectorView("detail");
    if (!inspectorOpen) {
      setInspectorOpen(true);
      writePreviewFlag(true);
    }
  };

  /**
   * Pull rates from a costed line: hand it to the pull-rates strip directly
   * below the table, which maps the indexed hours and calls
   * POST /api/rate-cards/derive. No rate maths happens here or in the table.
   */
  const handlePullRates = (item) => {
    // Wrapped with a fresh pull id every time. The strip prefills off an
    // identity change, so pulling the SAME row again after clearing it has to
    // look like a new pull — otherwise the second click does nothing.
    //
    // The scope rides along: on "Actual (Island Time)" the owner is reading
    // shop hours, so the pull has to fill from shop hours. "Historic estimate"
    // and "Both" stay on the quoted columns — deriving off actuals happens only
    // when it was deliberately scoped that way.
    pullSeq.current += 1;
    setPulledReference({
      item,
      pullId: pullSeq.current,
      laborSource: referenceScope === "act" ? "actual" : "quoted",
      // The inspector action says "Pull rates", so it must complete the
      // derive-and-apply flow. The lower form remains editable and its own
      // button still supports manual recalculation after adjustments.
      autoApply: true,
    });
  };

  const buildSharedPayload = () => ({
    category: form.category.trim(),
    material: form.material.trim() === "" ? null : form.material.trim(),
    default_material_thickness_in: numOrNull(
      form.default_material_thickness_in,
    ),
    default_finish_rate_id: numOrNull(form.default_finish_rate_id),
    notes: form.notes,
    labor_basis: form.labor_basis,
    geometry_method: form.geometry_method,
    min_labor_hours: numOrNull(form.min_labor_hours),
    active: form.active,
    status: form.status,
    source: form.source === "" ? null : form.source,
  });

  const buildUnbandedPayload = () => ({
    ...buildSharedPayload(),
    cnc_per_sqft: numOrNull(form.cnc_per_sqft),
    cut_per_sqft: numOrNull(form.cut_per_sqft),
    fab_per_sqft: numOrNull(form.fab_per_sqft),
    paint_per_sqft: numOrNull(form.paint_per_sqft),
    size_band_dim: null,
    size_band_min_in: null,
    size_band_max_in: null,
    band_label: null,
  });

  const buildBandPayload = (row) => ({
    ...buildSharedPayload(),
    cnc_per_sqft: numOrNull(row.cnc_per_sqft),
    cut_per_sqft: numOrNull(row.cut_per_sqft),
    fab_per_sqft: numOrNull(row.fab_per_sqft),
    paint_per_sqft: numOrNull(row.paint_per_sqft),
    size_band_dim: bandDim,
    size_band_min_in: numOrNull(row.size_band_min_in),
    size_band_max_in: numOrNull(row.size_band_max_in),
    band_label:
      row.band_label && row.band_label.trim() ? row.band_label.trim() : null,
  });

  const reselectAfterSave = (rateCardData, { category, dim }) => {
    const list = rateCardData?.labor_cards || [];
    if (dim) {
      const family = list
        .filter((c) => c.category === category && c.size_band_dim === dim)
        .sort(byBandMin);
      if (family.length) {
        setForm(cardToForm(family[0]));
        setBanded(true);
        setBandDim(dim);
        setBands(family.map(cardToRow));
        setLoadedFamily(family);
        return;
      }
    }
    const single = list.find(
      (c) => c.category === category && !c.size_band_dim,
    );
    if (single) handleSelectCard(single);
  };

  const saveBanded = async () => {
    if (!bandDim) {
      setSaveError("Choose what to band by.");
      return;
    }
    const keptIds = new Set(bands.filter((r) => r.id).map((r) => r.id));
    const deletions = loadedFamily.filter((c) => !keptIds.has(c.id));
    if (deletions.length) {
      const list = deletions.map((c) => `• ${describeCard(c)}`).join("\n");
      if (
        !window.confirm(
          `Saving will delete these bands:\n\n${list}\n\nContinue?`,
        )
      ) {
        return;
      }
    }
    setSaving(true);
    try {
      const ops = [];
      bands.forEach((row) => {
        const payload = buildBandPayload(row);
        ops.push(
          row.id ? updateLaborCard(row.id, payload) : createLaborCard(payload),
        );
      });
      deletions.forEach((c) => ops.push(deleteLaborCard(c.id)));
      await Promise.all(ops);
      const rc = await loadAll();
      reselectAfterSave(rc, { category: form.category.trim(), dim: bandDim });
      const n = bands.length;
      setSaveSuccess(`Saved ${n} band${n === 1 ? "" : "s"}.`);
    } catch (err) {
      setSaveError(err.error || err.message || "Failed to save bands.");
    } finally {
      setSaving(false);
    }
  };

  const saveUnbanded = async () => {
    const keptId = form.id || null;
    const deletions = loadedFamily.filter((c) => c.id !== keptId);
    if (deletions.length) {
      const list = deletions.map((c) => `• ${describeCard(c)}`).join("\n");
      if (
        !window.confirm(
          `Switching to one rate will delete these bands:\n\n${list}\n\nContinue?`,
        )
      ) {
        return;
      }
    }
    setSaving(true);
    try {
      const payload = buildUnbandedPayload();
      const saved = keptId
        ? await updateLaborCard(keptId, payload)
        : await createLaborCard(payload);
      await Promise.all(deletions.map((c) => deleteLaborCard(c.id)));
      await loadAll();
      setForm(cardToForm(saved));
      setBanded(false);
      setBandDim("");
      setBands([]);
      setLoadedFamily([saved]);
      setSaveSuccess(keptId ? "Rate card updated." : "Rate card created.");
    } catch (err) {
      setSaveError(err.error || err.message || "Failed to save rate card.");
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    setSaveError(null);
    setSaveSuccess(null);
    if (!form.category.trim()) {
      setSaveError("Category is required.");
      return;
    }
    if (banded) {
      await saveBanded();
    } else {
      await saveUnbanded();
    }
  };

  const handleDelete = async () => {
    // In banded mode, delete removes the whole family.
    if (banded) {
      if (loadedFamily.length === 0) {
        handleNewCard();
        return;
      }
      const list = loadedFamily.map((c) => `• ${describeCard(c)}`).join("\n");
      if (
        !window.confirm(`Delete all ${loadedFamily.length} bands?\n\n${list}`)
      ) {
        return;
      }
      setSaveError(null);
      setSaveSuccess(null);
      setSaving(true);
      try {
        await Promise.all(loadedFamily.map((c) => deleteLaborCard(c.id)));
        await loadAll();
        handleNewCard();
      } catch (err) {
        setSaveError(err.error || err.message || "Failed to delete bands.");
      } finally {
        setSaving(false);
      }
      return;
    }
    if (!form.id) return;
    if (!window.confirm("Delete this rate card? This cannot be undone."))
      return;
    setSaveError(null);
    setSaveSuccess(null);
    setSaving(true);
    try {
      await deleteLaborCard(form.id);
      await loadAll();
      handleNewCard();
    } catch (err) {
      setSaveError(err.error || err.message || "Failed to delete rate card.");
    } finally {
      setSaving(false);
    }
  };

  /**
   * The one-click approval. Flips every card in the current selection to
   * `confirmed`, then reloads so the rail badge and the banner update
   * together. The SERVER stamps `rate_confirmed_at` on the status flip —
   * the client never sends a date, so approval times can't drift with a
   * wrong local clock.
   */
  const handleApprove = async () => {
    const targets = banded
      ? loadedFamily
      : form.id
        ? loadedFamily.filter((c) => c.id === form.id)
        : [];
    if (targets.length === 0) return;
    if (
      targets.length > 1 &&
      !window.confirm(
        `Approve all ${targets.length} bands of “${form.category}”?\n\n` +
          `Every band is marked confirmed and dated today.`,
      )
    ) {
      return;
    }

    setSaveError(null);
    setSaveSuccess(null);
    setSaving(true);
    try {
      const results = await Promise.allSettled(
        targets.map((c) => updateLaborCard(c.id, { status: "confirmed" })),
      );
      const failed = targets.filter((_, i) => results[i].status === "rejected");

      const rc = await loadAll();
      const list = rc?.labor_cards || [];
      if (banded) {
        reselectAfterSave(rc, { category: form.category.trim(), dim: bandDim });
      } else {
        const fresh = list.find((c) => c.id === form.id);
        if (fresh) {
          setForm(cardToForm(fresh));
          setLoadedFamily([fresh]);
        }
      }

      // Never a silent partial success: name every band that did not take.
      if (failed.length) {
        setSaveError(
          `Approved ${targets.length - failed.length} of ${targets.length}. ` +
            `Still needs approval: ${failed.map(describeCard).join(", ")}.`,
        );
      } else {
        setSaveSuccess(
          targets.length > 1
            ? `Approved all ${targets.length} bands.`
            : "Card approved.",
        );
      }
    } catch (err) {
      setSaveError(err.error || err.message || "Failed to approve.");
    } finally {
      setSaving(false);
    }
  };

  // Finish id → name, for showing a card's default finish in the rail. Keyed
  // by both number and string id so it tolerates however the API serializes it.
  const finishNameById = useMemo(() => {
    const map = new Map();
    finishRates.forEach((f) => {
      map.set(f.id, f.name);
      map.set(String(f.id), f.name);
      map.set(Number(f.id), f.name);
    });
    return map;
  }, [finishRates]);

  // Group the rail: each category holds banded families (one entry each) and
  // standalone single cards.
  const railGroups = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = q
      ? laborCards.filter((c) => {
          const band = `${c.band_label || ""} ${bandLabel(c)}`.toLowerCase();
          return (
            (c.category || "").toLowerCase().includes(q) ||
            (c.material || "").toLowerCase().includes(q) ||
            (c.labor_basis || "").toLowerCase().includes(q) ||
            band.includes(q)
          );
        })
      : laborCards;
    const byCat = {};
    filtered.forEach((c) => {
      const key = c.category || "(uncategorized)";
      if (!byCat[key]) byCat[key] = [];
      byCat[key].push(c);
    });
    return Object.keys(byCat)
      .sort()
      .map((category) => {
        const cards = byCat[category];
        const famMap = {};
        const singles = [];
        cards.forEach((c) => {
          if (c.size_band_dim) {
            if (!famMap[c.size_band_dim]) famMap[c.size_band_dim] = [];
            famMap[c.size_band_dim].push(c);
          } else {
            singles.push(c);
          }
        });
        const families = Object.keys(famMap)
          .sort()
          .map((dim) => ({ dim, cards: famMap[dim].slice().sort(byBandMin) }));
        return { category, families, singles };
      });
  }, [laborCards, search]);

  // Registry keys drive which rail groups are "legacy" (not a known template).
  const registryKeys = useMemo(
    () => new Set(productTypes.map((t) => t.key)),
    [productTypes],
  );

  // One rail row per registry product type, tagged with its coverage. A type is
  // "priced" when any labor card's category matches its key exactly (the same
  // exact-match the pricing resolver uses). Filtered by the shared search box.
  const templateRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    const byCat = {};
    laborCards.forEach((c) => {
      if (!c.category) return;
      if (!byCat[c.category]) byCat[c.category] = [];
      byCat[c.category].push(c);
    });
    return productTypes
      .filter((t) => {
        if (!q) return true;
        return (
          (t.display_name || "").toLowerCase().includes(q) ||
          (t.key || "").toLowerCase().includes(q)
        );
      })
      .map((t) => {
        const cards = byCat[t.key] || [];
        const famMap = {};
        const singles = [];
        cards.forEach((c) => {
          if (c.size_band_dim) {
            if (!famMap[c.size_band_dim]) famMap[c.size_band_dim] = [];
            famMap[c.size_band_dim].push(c);
          } else {
            singles.push(c);
          }
        });
        const families = Object.keys(famMap)
          .sort()
          .map((dim) => ({ dim, cards: famMap[dim].slice().sort(byBandMin) }));
        const primaryFamily = families[0] || null;
        return {
          type: t,
          covered: cards.length > 0,
          families,
          singles,
          primaryFamily,
          bandCount: primaryFamily ? primaryFamily.cards.length : 0,
          // What the owner sees without opening anything.
          rollup: rollUpApproval(cards),
        };
      });
  }, [productTypes, laborCards, search]);

  // Category groups that aren't known templates — legacy/uncategorized cards
  // stay reachable here; registry categories live in the templates section.
  const legacyGroups = useMemo(
    () =>
      railGroups
        .filter((g) => !registryKeys.has(g.category))
        .map((g) => ({
          ...g,
          rollup: rollUpApproval([
            ...g.families.flatMap((f) => f.cards),
            ...g.singles,
          ]),
        })),
    [railGroups, registryKeys],
  );

  // The owner's action list: every rail row, template or legacy, that still
  // has a card nobody has signed off. Order matches the rail.
  const approvalEntries = useMemo(
    () => [
      ...templateRows.map((row) => ({
        key: `tpl-${row.type.key}`,
        label: row.type.display_name,
        rollup: row.rollup,
        select: () => handleSelectTemplate(row),
      })),
      ...legacyGroups.map((group) => ({
        key: `grp-${group.category}`,
        label: group.category,
        rollup: group.rollup,
        select: () =>
          group.families.length
            ? handleSelectFamily(group.category, group.families[0].dim)
            : group.singles.length
              ? handleSelectCard(group.singles[0])
              : undefined,
      })),
    ],
    // handleSelect* are stable enough for this screen (plain closures over
    // state setters); the data they read is in the deps below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [templateRows, legacyGroups, laborCards],
  );

  const approvalQueue = useMemo(
    () => summarizeApprovalQueue(approvalEntries),
    [approvalEntries],
  );

  // Filtering is only ever offered while something is actually pending.
  const filterPending = onlyPending && approvalQueue.pendingCount > 0;
  const visibleTemplateRows = filterPending
    ? templateRows.filter((row) => isPendingApproval(row.rollup))
    : templateRows;
  const visibleLegacyGroups = filterPending
    ? legacyGroups.filter((group) => isPendingApproval(group.rollup))
    : legacyGroups;

  // Click the queue: narrow the rail to what needs him, and open the first one.
  const handleOpenQueue = () => {
    if (!approvalQueue.first) return;
    setOnlyPending(true);
    approvalQueue.first.select();
  };

  const bandWarnings = useMemo(
    () => (banded ? computeBandWarnings(bands) : []),
    [banded, bands],
  );

  const previewFormValues = useMemo(() => {
    const base = {
      geometry_method: form.geometry_method,
      labor_basis: form.labor_basis,
      min_labor_hours: form.min_labor_hours,
    };
    if (banded) {
      return {
        ...base,
        banded: true,
        band_dim: bandDim,
        bands: bands.map((r) => ({
          band_label: r.band_label,
          size_band_min_in: r.size_band_min_in,
          size_band_max_in: r.size_band_max_in,
          rates: {
            cnc: r.cnc_per_sqft,
            cut: r.cut_per_sqft,
            fab: r.fab_per_sqft,
            paint: r.paint_per_sqft,
          },
        })),
        labor_rates: {},
      };
    }
    return {
      ...base,
      banded: false,
      labor_rates: {
        cnc: form.cnc_per_sqft,
        cut: form.cut_per_sqft,
        fab: form.fab_per_sqft,
        paint: form.paint_per_sqft,
      },
    };
  }, [form, banded, bandDim, bands]);

  const unitSuffix = unitForBasis(form.labor_basis);

  // An editing card whose category isn't a known template (legacy) still needs
  // an option in the select so it stays editable and doesn't silently reset.
  const categoryIsLegacy =
    form.category !== "" && !productTypes.some((t) => t.key === form.category);

  const legacyBasisLabel = LEGACY_BASIS_LABELS[form.labor_basis];
  const pricedByOptions = legacyBasisLabel
    ? [
        ...PRICED_BY_OPTIONS,
        { value: form.labor_basis, label: legacyBasisLabel, legacy: true },
      ]
    : PRICED_BY_OPTIONS;

  const bandCount = bands.length;
  const saveLabel = saving
    ? "Saving…"
    : banded
      ? `Save ${bandCount} band${bandCount === 1 ? "" : "s"}`
      : form.id
        ? "Save card"
        : "Create card";

  if (loading) {
    return (
      <WorkbenchShell
        variant="admin"
        title="Rate cards"
        center={<p className="wb-empty">Loading rate cards…</p>}
      />
    );
  }

  /* ── Rail: the collection ─ product templates, card families, finishes ── */
  const rail = (
    <div className="rate-card-rail">
      {/* The owner's action list. Always present: pending work, or all-clear.
          Sticky so it stays put while he scrolls a long list of templates. */}
      <div className="rate-card-rail__queue">
        {approvalQueue.pendingCount > 0 ? (
          <div className="rc-queue rc-queue--pending">
            <button
              type="button"
              className="wb-plain rc-queue__cta"
              onClick={handleOpenQueue}
            >
              <AlertCircle className="rc-queue__icon" aria-hidden="true" />
              <span className="rc-queue__text">
                {approvalQueueHeadline(approvalQueue.pendingCount)}
              </span>
              <ChevronRight className="rc-queue__chevron" aria-hidden="true" />
            </button>
            {filterPending && (
              <button
                type="button"
                className="wb-plain rc-queue__reset"
                onClick={() => setOnlyPending(false)}
              >
                Show all rate cards
              </button>
            )}
          </div>
        ) : (
          <div className="rc-queue rc-queue--clear">
            <Check className="rc-queue__icon" aria-hidden="true" />
            <span className="rc-queue__text">All rate cards approved</span>
          </div>
        )}
      </div>

      <div className="rate-card-rail__controls">
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Filter cards…"
          className="rate-card-rail__search h-9"
        />
      </div>

      <div className="rate-card-rail__list">
        {/* Product templates — every registry type, priced or not. */}
        {visibleTemplateRows.length > 0 && (
          <div className="rate-card-rail__group rate-card-rail__templates">
            <div className="rc-group__hd">
              <span className="rc-group__hd-label">Product templates</span>
            </div>
            {visibleTemplateRows.map((row) => {
              const isActive = form.category === row.type.key;
              return (
                <button
                  key={`tpl-${row.type.key}`}
                  type="button"
                  onClick={() => handleSelectTemplate(row)}
                  className={rowClass(isActive)}
                >
                  <span className="rc-row__text">
                    <span className="rc-row__name">
                      {row.type.display_name}
                    </span>
                    <span className="rc-row__sub">{coverageLabel(row)}</span>
                  </span>
                  <ApprovalBadge
                    rollup={row.rollup}
                    className="rc-row__status"
                  />
                </button>
              );
            })}
          </div>
        )}

        {visibleTemplateRows.length === 0 &&
          visibleLegacyGroups.length === 0 && (
            <p className="rc-empty">
              {filterPending
                ? "Nothing left to approve here."
                : search.trim()
                  ? "No matches."
                  : "No cards yet. Create one to start."}
            </p>
          )}
        {visibleLegacyGroups.map((group) => (
          <div key={group.category} className="rate-card-rail__group">
            <div className="rc-group__hd">
              <span className="rc-group__hd-label">{group.category}</span>
              <ApprovalBadge rollup={group.rollup} className="rc-row__status" />
            </div>
            {group.families.map((fam) => {
              const isActive =
                banded &&
                form.category === group.category &&
                bandDim === fam.dim;
              const bandNames = fam.cards
                .map((c) => c.band_label || bandLabel(c) || "band")
                .join(" · ");
              return (
                <button
                  key={`fam-${fam.dim}`}
                  type="button"
                  onClick={() => handleSelectFamily(group.category, fam.dim)}
                  className={rowClass(isActive)}
                >
                  <span className="rc-row__text">
                    <span className="rc-row__name">
                      {bandDimLabel(fam.dim)} bands
                    </span>
                    <span className="rc-row__sub">
                      {fam.cards.length} bands · {bandNames}
                    </span>
                  </span>
                </button>
              );
            })}

            {group.singles.map((card) => {
              const isActive = !banded && form.id === card.id;
              const finishName =
                card.default_finish_rate_id != null
                  ? finishNameById.get(card.default_finish_rate_id)
                  : null;
              // One muted line instead of a dot plus three stacked labels.
              const detail = [
                card.active ? null : "Draft",
                card.material || null,
                finishName ? `Finish: ${finishName}` : null,
                LABOR_BASIS_SHORT[card.labor_basis] || "sqft",
              ]
                .filter(Boolean)
                .join(" · ");
              return (
                <button
                  key={card.id}
                  type="button"
                  onClick={() => handleSelectCard(card)}
                  className={rowClass(isActive)}
                >
                  <span className="rc-row__text">
                    <span className="rc-row__name">{card.category}</span>
                    <span className="rc-row__sub">{detail}</span>
                  </span>
                </button>
              );
            })}
          </div>
        ))}
      </div>

      <div className="rate-card-rail__finishes">
        <FinishesSection finishRates={finishRates} onChanged={loadAll} />
      </div>
    </div>
  );

  /* ── Centre: the selected card's editor ── */
  const center = (
    <div className="rate-card-form">
      {loadError && (
        <div className="rate-card-alert rate-card-alert--error">
          {loadError}
        </div>
      )}

      <div className="rate-card-form__body space-y-8">
        {saveError && (
          <div className="rate-card-alert rate-card-alert--error">
            {saveError}
          </div>
        )}
        {saveSuccess && (
          <div className="rate-card-alert rate-card-alert--good">
            {saveSuccess}
          </div>
        )}

        {/* The approval decision, at the top where it can't be missed. */}
        <ApprovalBanner
          cards={
            banded ? loadedFamily : loadedFamily.filter((c) => c.id === form.id)
          }
          confirmedAt={form.rate_confirmed_at}
          busy={saving}
          onApprove={handleApprove}
        />

        {/* Group: Product */}
        <FormGroup title="Product">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label="Category" required>
              <select
                value={form.category}
                onChange={(e) => setField("category", e.target.value)}
                className="h-10 w-full rounded-md border border-[var(--input-border-color)] bg-[var(--input-bg-color)] px-3 text-sm text-[var(--input-text-color)]"
              >
                <option value="">Select a product type…</option>
                {productTypes.map((t) => (
                  <option key={t.key} value={t.key}>
                    {t.display_name}
                  </option>
                ))}
                {categoryIsLegacy && (
                  <option value={form.category}>
                    {form.category} (legacy)
                  </option>
                )}
              </select>
            </Field>
            <Field label="Material scope">
              <select
                value={form.material}
                onChange={(e) => setField("material", e.target.value)}
                className="h-10 w-full rounded-md border border-[var(--input-border-color)] bg-[var(--input-bg-color)] px-3 text-sm text-[var(--input-text-color)]"
              >
                <option value="">Any material</option>
                {materials.map((m) => (
                  <option key={m.id} value={m.name}>
                    {m.name}
                  </option>
                ))}
              </select>
            </Field>
          </div>
          <Field
            label="Default material thickness"
            hint="in"
            belowHint="When an estimator picks just a material class (e.g. PVC), the price uses this thickness. The exact sheet used always shows on the quote line."
          >
            <div className="flex flex-wrap items-center gap-2">
              <Input
                type="number"
                min="0"
                step="0.125"
                value={form.default_material_thickness_in}
                onChange={(e) =>
                  setField("default_material_thickness_in", e.target.value)
                }
                onFocus={(e) => e.target.select()}
                placeholder="e.g. 0.5"
                className="w-32 text-right tabular-nums"
              />
              <ThicknessChip
                label={"½″"}
                value="0.5"
                current={form.default_material_thickness_in}
                onPick={(v) => setField("default_material_thickness_in", v)}
              />
              <ThicknessChip
                label={"¾″"}
                value="0.75"
                current={form.default_material_thickness_in}
                onPick={(v) => setField("default_material_thickness_in", v)}
              />
            </div>
          </Field>
          <Field
            label="Default finish"
            belowHint="Applied when an estimator picks the Island Armor Finish on a line priced by this card. Estimators can still switch a line to finish-by-others."
          >
            <select
              value={form.default_finish_rate_id}
              onChange={(e) =>
                setField("default_finish_rate_id", e.target.value)
              }
              className="h-10 w-full rounded-md border border-[var(--input-border-color)] bg-[var(--input-bg-color)] px-3 text-sm text-[var(--input-text-color)]"
            >
              <option value="">No finish default</option>
              {finishRates.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.name} — {formatMoney(f.cost_per_sqft)}/sqft
                </option>
              ))}
            </select>
          </Field>
          <Field label="Notes">
            <Textarea
              rows={2}
              value={form.notes}
              onChange={(e) => setField("notes", e.target.value)}
              placeholder="Optional context for this rate card"
            />
          </Field>
        </FormGroup>

        {/* Group: Priced by */}
        <FormGroup title="Priced by" hint="How this product's rates scale.">
          <RadioChips
            options={pricedByOptions}
            value={form.labor_basis}
            onChange={(v) => setField("labor_basis", v)}
          />
          {form.labor_basis === "sqft" && (
            <p className="text-xs text-[var(--text-color)] opacity-60">
              {sqftSourceLabel(form.geometry_method)}
            </p>
          )}
        </FormGroup>

        {/* Group: Labor + size bands (hero) */}
        <FormGroup
          title="Labor by department"
          hint="Columns are the house example: Small 6–12″, Medium 12–16″, Large 16–20″ — each band carries its own rates."
          elevated
        >
          <RadioChips
            options={[
              { value: "one", label: "One rate for all sizes" },
              { value: "banded", label: "Rates change by size band" },
            ]}
            value={banded ? "banded" : "one"}
            onChange={(v) => handleSetBanded(v === "banded")}
          />

          {!banded ? (
            <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
              {LABOR_DEPTS.map((dept) => (
                <Field key={dept} label={dept.toUpperCase()} hint={unitSuffix}>
                  <Input
                    type="number"
                    min="0"
                    step="0.00001"
                    value={form[`${dept}_per_sqft`]}
                    onChange={(e) =>
                      setField(`${dept}_per_sqft`, e.target.value)
                    }
                    onFocus={(e) => e.target.select()}
                    className="text-right tabular-nums"
                  />
                </Field>
              ))}
            </div>
          ) : (
            <BandEditor
              bandDim={bandDim}
              onBandDim={setBandDim}
              bands={bands}
              unitSuffix={unitSuffix}
              onRowChange={setBandRow}
              onAddBand={addBand}
              onRemoveBand={removeBand}
              warnings={bandWarnings}
            />
          )}

          {/* Evidence and the fields it fills, in one collapsible section
              right where the hours get typed. Rendered banded or not — a size
              band needs the same past jobs a single rate does. */}
          <PullRatesSection
            expanded={referencesExpanded}
            onToggle={toggleReferences}
            results={referenceResults}
            scope={referenceScope}
            onScopeChange={setReferenceScope}
            selectedId={selectedReference ? selectedReference.id : null}
            onSelect={handleSelectReference}
            onResults={setReferenceResults}
            banded={banded}
            geometryMethod={form.geometry_method}
            laborBasis={form.labor_basis}
            pickedItem={pulledReference}
            onApply={applyDerivedRates}
          />
        </FormGroup>

        {/* Group: Minimum labor floor */}
        <FormGroup title="Minimum labor floor">
          <Field
            label="Floor (hours)"
            belowHint="Small pieces never price below this many shop hours."
          >
            <Input
              type="number"
              min="0"
              step="0.25"
              value={form.min_labor_hours}
              onChange={(e) => setField("min_labor_hours", e.target.value)}
              onFocus={(e) => e.target.select()}
              className="w-40 text-right tabular-nums"
            />
          </Field>
        </FormGroup>

        {/* Group: Status */}
        <FormGroup title="Status">
          <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-[var(--border-color)] bg-[var(--input-bg-color)] p-3">
            <input
              type="checkbox"
              checked={form.active}
              onChange={(e) => setField("active", e.target.checked)}
              className="mt-0.5 h-4 w-4 shrink-0"
            />
            <span className="text-sm leading-snug text-[var(--text-color)]">
              <span className="font-semibold">Live</span> — this card prices
              real quotes. Uncheck to keep it as a draft while you tune it.
            </span>
          </label>

          <div className="flex flex-col gap-1.5 sm:max-w-md">
            <Field
              label="Change status"
              belowHint="Approving is one click at the top of this card; this is here for the other labels."
            >
              <select
                value={form.status}
                onChange={(e) => setField("status", e.target.value)}
                className="h-10 w-full rounded-md border border-[var(--input-border-color)] bg-[var(--input-bg-color)] px-3 text-sm text-[var(--input-text-color)]"
              >
                {STATUS_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
            </Field>
            <p className="text-xs leading-snug text-[var(--text-color)] opacity-70">
              {STATUS_DESCRIPTIONS[form.status]}
            </p>
          </div>

          {form.source && (
            <Field label="Source">
              <div className="flex h-10 items-center text-sm opacity-70">
                {form.source}
              </div>
            </Field>
          )}

          <p className="text-xs italic leading-snug text-[var(--text-color)] opacity-55">
            Status is a bookkeeping label; only Live/draft affects whether the
            card prices quotes.
          </p>
        </FormGroup>
      </div>
    </div>
  );

  const cardTitle = banded
    ? loadedFamily.length
      ? `Editing ${form.category || "family"} bands`
      : "New banded family"
    : form.id
      ? `Editing card #${form.id}`
      : "New rate card";

  // The navbar says where you are and whether this card is live — nothing
  // else. Band count, material and the approval stamp all already have a home
  // in the editor itself, and the top bar could not fit them without clipping.
  const statusLabel = form.active ? "Live" : "Draft";
  const crumbCategory = form.category
    ? productTypes.find((t) => t.key === form.category)?.display_name ||
      form.category
    : null;

  const showReferenceDetail = inspectorView === "detail" && !!selectedReference;

  /* Straight from POST /api/rate-cards/preview — the endpoint's own `pricing`
     block for the Typical scenario. Nothing here recomputes a dollar. */
  const statusBar = (
    <>
      <StatusCell
        label="Labor"
        value={previewMoney(previewPricing?.labor_cost)}
      />
      <StatusCell
        label="Material"
        value={previewMoney(previewPricing?.material_cost)}
      />
      <StatusCell
        label="Overhead"
        value={previewMoney(previewPricing?.overhead)}
      />
      <StatusCell
        label="Break even"
        value={previewMoney(previewPricing?.break_even)}
      />
      <StatusCell
        label="Sell price"
        value={previewMoney(previewPricing?.sell_price)}
        title="The Typical preview scenario, per unit."
        emphasis
      />
    </>
  );

  return (
    <>
      <WorkbenchShell
        variant="admin"
        wideRail
        title="Rate cards"
        subtitle={crumbCategory}
        meta={<span className="wb-pill">{statusLabel}</span>}
        actions={
          <button
            type="button"
            className="wb-tbtn"
            onClick={() => setShowHistorical(true)}
            title="Research and correct the indexed costing-sheet lines"
          >
            <Database className="wb-tbtn__icon" /> Historical costs
          </button>
        }
        railHeader={{
          label: "Rate cards",
          count: laborCards.length,
          action: (
            <button type="button" className="wb-tbtn" onClick={handleNewCard}>
              <Plus className="wb-tbtn__icon" /> New
            </button>
          ),
        }}
        rail={rail}
        centerHeader={{
          label: cardTitle,
          action: (
            <>
              {(banded ? loadedFamily.length > 0 : form.id) ? (
                <button
                  type="button"
                  className="wb-tbtn rate-card-delete"
                  disabled={saving}
                  onClick={handleDelete}
                >
                  Delete
                </button>
              ) : null}
              <button
                type="button"
                className="wb-tbtn wb-tbtn--solid"
                onClick={handleSave}
                disabled={saving}
              >
                {saveLabel}
              </button>
            </>
          ),
        }}
        center={center}
        inspectorHeader={{
          label: showReferenceDetail ? "Reference detail" : "Live preview",
          // Only offered once a costed line has been picked — there is
          // nothing to swap to before that.
          action: selectedReference ? (
            <button
              type="button"
              className="wb-tbtn"
              onClick={() =>
                setInspectorView(showReferenceDetail ? "preview" : "detail")
              }
            >
              {showReferenceDetail ? "Live preview" : "Reference detail"}
            </button>
          ) : null,
        }}
        inspector={
          showReferenceDetail ? (
            <CostedJobReferenceDetail
              item={selectedReference}
              scope={referenceScope}
              canPullRates={!banded}
              onPullRates={handlePullRates}
            />
          ) : (
            <RateCardPreviewRail
              formValues={previewFormValues}
              materials={materials}
              finishRates={finishRates}
              onPricing={setPreviewPricing}
            />
          )
        }
        inspectorOpen={inspectorOpen}
        onToggleInspector={toggleInspector}
        inspectorToggleLabel={
          showReferenceDetail ? "Reference detail" : "Live preview"
        }
        statusBar={statusBar}
      />

      <HistoricalSearchDialog
        isOpen={showHistorical}
        onClose={() => setShowHistorical(false)}
      />
    </>
  );
}

/* ---------- "Pull rates from a costed job": one section, evidence + inputs ---------- */

/**
 * A closed section has to earn its click. Before anything has loaded the
 * header says what is inside in one line; once the table has been opened
 * (results survive collapsing) it carries the numbers instead — how many
 * costed lines, how many have Island Time hours behind them, and the median
 * cushion across them.
 */
function referencesScent(results) {
  if (!results.length) {
    return "Quoted labor from historical estimates, side by side with actual shop hours from Island Time.";
  }
  const withActuals = countWithActuals(results);
  const median = medianCushion(results);
  // The median only counts verified-evidence lines, so name that population
  // instead of letting the number sit beside a count it does not summarize.
  const verified = results.filter(isVerifiedCushion).length;
  return [
    `${results.length} costed line${results.length === 1 ? "" : "s"}`,
    `${withActuals} with shop hours`,
    median === null
      ? null
      : `median ${formatCushion(median)} cushion across ${verified} verified`,
  ]
    .filter(Boolean)
    .join(" · ");
}

/**
 * The evidence and the inputs it feeds, in one collapsible section sitting
 * with the department-hours fields.
 *
 * Two zones, one frame: the costed-job table on top, the fields it fills
 * directly beneath. Pulling rates from a row moves numbers down the screen —
 * no navigation, no second panel to open, and no browse button pointing at a
 * table that is already on screen.
 */
function PullRatesSection({
  expanded,
  onToggle,
  results,
  scope,
  onScopeChange,
  selectedId,
  onSelect,
  onResults,
  banded,
  geometryMethod,
  laborBasis,
  pickedItem,
  onApply,
}) {
  return (
    <div className="overflow-hidden rounded-lg border border-[var(--border-color)] bg-[var(--card-bg)]">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        // wb-plain: opt out of the global button chrome (index.css margin +
        // hover lift/ring) — a full-bleed header inside an overflow-hidden
        // card clips the ring at the edges. Same fix as the rail rows.
        className="wb-plain flex w-full items-start gap-2 px-3 py-2.5 text-left transition-colors hover:bg-[var(--hover-bg)]"
      >
        {expanded ? (
          <ChevronDown className="mt-0.5 h-4 w-4 shrink-0 text-[var(--primary-color)]" />
        ) : (
          <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-[var(--primary-color)]" />
        )}
        <Table2 className="mt-0.5 h-4 w-4 shrink-0 text-[var(--text-color)] opacity-60" />
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-semibold text-[var(--text-color)]">
            Pull rates from a costed job
          </span>
          <span className="mt-0.5 block text-xs leading-snug text-[var(--text-color)] opacity-70">
            {referencesScent(results)}
          </span>
        </span>
      </button>

      {expanded && (
        <>
          <div className="border-t border-[var(--border-color)] px-3 pb-3 pt-3">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-[var(--text-color)] opacity-70">
              Costed job references
            </p>
            <CostedJobReferences
              scope={scope}
              onScopeChange={onScopeChange}
              selectedId={selectedId}
              onSelect={onSelect}
              onResults={onResults}
            />
          </div>

          {/* The fields the table feeds. Tinted so the reading zone and the
              typing zone are told apart without a second bordered card. */}
          <div className="border-t border-[var(--border-color)] bg-[color:var(--secondary-color)] px-3 py-3">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-[var(--text-color)] opacity-70">
              Fill the card
            </p>
            {banded ? (
              <p className="text-sm text-[var(--text-color)] opacity-80">
                Pulling rates fills a single-rate card. Switch this card off
                size bands to pull from a line above, or read the hours in the
                table and type the band you are tuning.
              </p>
            ) : (
              <RateCardDeriveStrip
                geometryMethod={geometryMethod}
                laborBasis={laborBasis}
                onApply={onApply}
                pickedItem={pickedItem}
              />
            )}
          </div>
        </>
      )}
    </div>
  );
}

/* ---------- Approval banner (top of the editor) ---------- */

/**
 * The card's standing, in plain English, with the approval as one button.
 *
 * Hidden for an unsaved card: there is nothing to approve until it exists.
 * For a banded family every band is a separate card row, so approving offers
 * to sign off the whole family at once.
 */
function ApprovalBanner({ cards, confirmedAt, busy, onApprove }) {
  const cardCount = cards.length;
  if (!cardCount) return null;

  // Driven by what is SAVED, not by the unsaved status select below.
  const rollup = rollUpApproval(cards);
  const approved = rollup.state === APPROVAL_STATES.APPROVED;
  const tone = APPROVAL_TONE[rollup.state] || "needs-approval";
  const approvedOn = formatApprovedOn(confirmedAt);

  const status = approved
    ? "confirmed"
    : pendingStatusOf(cards) || "needs_adjustment";

  const title = approved
    ? approvedOn
      ? `Approved ${approvedOn}`
      : "Approved"
    : rollup.state === APPROVAL_STATES.CHECK_RATES
      ? "Check these rates"
      : "Needs your approval";

  // A family can be half-signed-off; say so rather than implying all-or-nothing.
  const partial =
    cardCount > 1 && rollup.pendingCount > 0 && rollup.pendingCount < cardCount
      ? `${rollup.pendingCount} of ${cardCount} bands still need approval.`
      : null;

  return (
    <section className={`rc-approve rc-approve--${tone}`}>
      <div className="rc-approve__text">
        <h3 className="rc-approve__title">{title}</h3>
        <p className="rc-approve__desc">
          {STATUS_DESCRIPTIONS[status]}
          {partial && ` ${partial}`}
        </p>
      </div>
      {!approved && (
        <div className="rc-approve__actions">
          <button
            type="button"
            className="wb-tbtn wb-tbtn--solid rc-approve__btn"
            onClick={onApprove}
            disabled={busy}
          >
            <Check className="wb-tbtn__icon" />
            {cardCount > 1
              ? `Approve all ${cardCount} bands`
              : "Approve this card"}
          </button>
        </div>
      )}
    </section>
  );
}

/* ---------- Band editor ---------- */

function BandEditor({
  bandDim,
  onBandDim,
  bands,
  unitSuffix,
  onRowChange,
  onAddBand,
  onRemoveBand,
  warnings,
}) {
  const gridTemplate = "minmax(120px,1.4fr) 84px 84px 74px 74px 74px 74px 36px";
  return (
    <div className="space-y-3">
      <div className="space-y-1.5">
        <span className="text-xs font-medium text-[var(--text-color)] opacity-80">
          Band by:
        </span>
        <div className="flex flex-wrap gap-2">
          {BAND_BY_CHIPS.map((chip) => {
            const selected = bandDim === chip.value;
            return (
              <button
                key={chip.value}
                type="button"
                onClick={() => onBandDim(chip.value)}
                style={
                  selected
                    ? {
                        backgroundColor:
                          "color-mix(in srgb, var(--primary-color) 13%, transparent)",
                        borderColor: "var(--primary-color)",
                        color: "var(--primary-color)",
                      }
                    : undefined
                }
                className={`wb-plain rounded-full border px-3 py-1.5 text-sm transition-colors ${
                  selected
                    ? "font-medium"
                    : "border-[var(--border-color)] text-[var(--text-color)] hover:bg-[var(--hover-bg-color)]"
                }`}
              >
                {chip.label}
              </button>
            );
          })}
        </div>
        {bandDim && BAND_BY_HINTS[bandDim] && (
          <p className="text-xs text-[var(--text-color)] opacity-60">
            {BAND_BY_HINTS[bandDim]}
          </p>
        )}
      </div>

      <div className="rate-card-band-scroll">
        <div style={{ minWidth: 700 }}>
          <div
            className="grid items-end gap-2 pb-1 text-[10px] font-semibold uppercase tracking-wide text-[var(--text-color)] opacity-60"
            style={{ gridTemplateColumns: gridTemplate }}
          >
            <span>Band</span>
            <span className="text-right">From (in)</span>
            <span className="text-right">To (in)</span>
            <span className="text-right">CNC</span>
            <span className="text-right">Cut</span>
            <span className="text-right">Fab</span>
            <span className="text-right">Paint</span>
            <span />
          </div>

          <div className="flex flex-col gap-2">
            {bands.map((row, index) => {
              const isLast = index === bands.length - 1;
              return (
                <div
                  key={row.id ?? `new-${index}`}
                  className="grid items-center gap-2"
                  style={{ gridTemplateColumns: gridTemplate }}
                >
                  <Input
                    value={row.band_label}
                    onChange={(e) =>
                      onRowChange(index, "band_label", e.target.value)
                    }
                    placeholder={defaultBandLabelFor(index)}
                    className="h-9 text-sm"
                  />
                  <Input
                    type="number"
                    min="0"
                    step="0.25"
                    value={row.size_band_min_in}
                    onChange={(e) =>
                      onRowChange(index, "size_band_min_in", e.target.value)
                    }
                    onFocus={(e) => e.target.select()}
                    className="h-9 text-right text-sm tabular-nums"
                  />
                  <Input
                    type="number"
                    min="0"
                    step="0.25"
                    value={row.size_band_max_in}
                    onChange={(e) =>
                      onRowChange(index, "size_band_max_in", e.target.value)
                    }
                    onFocus={(e) => e.target.select()}
                    placeholder={isLast ? "and up" : ""}
                    className="h-9 text-right text-sm tabular-nums"
                  />
                  {RATE_FIELDS.map((field) => (
                    <Input
                      key={field}
                      type="number"
                      min="0"
                      step="0.00001"
                      value={row[field]}
                      onChange={(e) =>
                        onRowChange(index, field, e.target.value)
                      }
                      onFocus={(e) => e.target.select()}
                      className="h-9 text-right text-sm tabular-nums"
                    />
                  ))}
                  <button
                    type="button"
                    onClick={() => onRemoveBand(index)}
                    disabled={bands.length <= 1}
                    title="Remove band"
                    className="wb-plain flex h-9 items-center justify-center rounded-md text-[var(--text-color)] opacity-60 hover:bg-[var(--hover-bg-color)] hover:opacity-100 disabled:cursor-not-allowed disabled:opacity-25"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <Button type="button" variant="outline" size="sm" onClick={onAddBand}>
          <Plus className="h-4 w-4" /> Add band
        </Button>
        <span className="text-xs text-[var(--text-color)] opacity-60">
          Rates in {unitSuffix}.
        </span>
      </div>

      {warnings.length > 0 && (
        <div className="space-y-1 rounded-md border border-amber-400/50 bg-amber-50 px-3 py-2">
          {warnings.map((w, i) => (
            <p key={i} className="text-xs text-amber-800">
              {w}
            </p>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------- small presentational helpers ---------- */

function FormGroup({ title, hint, elevated, children }) {
  const heroStyle = elevated
    ? {
        backgroundColor:
          "color-mix(in srgb, var(--primary-color) 6%, transparent)",
        borderColor:
          "color-mix(in srgb, var(--primary-color) 30%, var(--border-color))",
      }
    : undefined;
  return (
    <section
      style={heroStyle}
      className={elevated ? "space-y-3 rounded-lg border p-4" : "space-y-3"}
    >
      <div className="space-y-1">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-[var(--text-color)] opacity-60">
          {title}
        </h3>
        {hint && (
          <p className="text-xs leading-snug text-[var(--text-color)] opacity-70">
            {hint}
          </p>
        )}
      </div>
      {children}
    </section>
  );
}

function Field({ label, hint, belowHint, required, children }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-medium text-[var(--text-color)] opacity-80">
        {label}
        {required && (
          <span className="text-[color:var(--error-color)]"> *</span>
        )}
        {hint && <span className="ml-1 opacity-60">({hint})</span>}
      </span>
      {children}
      {belowHint && (
        <span className="text-[11px] leading-snug text-[var(--text-color)] opacity-60">
          {belowHint}
        </span>
      )}
    </label>
  );
}

/** Quick-pick chip that fills the thickness field with a standard answer. */
function ThicknessChip({ label, value, current, onPick }) {
  const selected =
    numOrNull(current) !== null && numOrNull(current) === numOrNull(value);
  const selectedTint = {
    backgroundColor:
      "color-mix(in srgb, var(--primary-color) 13%, transparent)",
    borderColor: "var(--primary-color)",
    color: "var(--primary-color)",
  };
  return (
    <button
      type="button"
      onClick={() => onPick(value)}
      style={selected ? selectedTint : undefined}
      className={`wb-plain rounded-full border px-3 py-1.5 text-sm transition-colors ${
        selected
          ? "font-medium"
          : "border-[var(--border-color)] text-[var(--text-color)] hover:bg-[var(--hover-bg-color)]"
      }`}
    >
      {label}
    </button>
  );
}

function RadioChips({ options, value, onChange }) {
  const selectedTint = {
    backgroundColor:
      "color-mix(in srgb, var(--primary-color) 13%, transparent)",
    borderColor: "var(--primary-color)",
    color: "var(--primary-color)",
  };
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((opt) => {
        const selected = value === opt.value;
        const chipClass = opt.legacy
          ? "border-dashed border-[var(--border-color)] bg-[var(--hover-bg-color)] text-[var(--text-color)] opacity-60"
          : selected
            ? "font-medium"
            : "border-[var(--border-color)] text-[var(--text-color)] hover:bg-[var(--hover-bg-color)]";
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => onChange(opt.value)}
            style={!opt.legacy && selected ? selectedTint : undefined}
            className={`wb-plain rounded-full border px-3 py-1.5 text-sm transition-colors ${chipClass}`}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

/* ---------- Finishes section (rail bottom) ---------- */

function blankFinish() {
  return {
    name: "",
    coats: "1",
    cost_per_gallon: "",
    coverage_sqft_per_gal_per_coat: "",
    active: true,
  };
}

function FinishesSection({ finishRates, onChanged }) {
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState(blankFinish);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const startEdit = (finish) => {
    setCreating(false);
    setEditingId(finish.id);
    setError(null);
    setDraft({
      name: toInput(finish.name),
      coats: toInput(finish.coats),
      cost_per_gallon: toInput(finish.cost_per_gallon),
      coverage_sqft_per_gal_per_coat: toInput(
        finish.coverage_sqft_per_gal_per_coat,
      ),
      active: finish.active !== false,
    });
  };

  const startCreate = () => {
    setEditingId(null);
    setCreating(true);
    setError(null);
    setDraft(blankFinish());
  };

  const cancel = () => {
    setEditingId(null);
    setCreating(false);
    setError(null);
  };

  const save = async () => {
    setError(null);
    if (!draft.name.trim()) {
      setError("Name is required.");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        name: draft.name.trim(),
        coats: Number(draft.coats) || 0,
        cost_per_gallon: Number(draft.cost_per_gallon) || 0,
        coverage_sqft_per_gal_per_coat:
          Number(draft.coverage_sqft_per_gal_per_coat) || 0,
        active: draft.active,
      };
      if (creating) {
        await createFinishRate(payload);
      } else {
        await updateFinishRate(editingId, payload);
      }
      await onChanged();
      cancel();
    } catch (err) {
      setError(err.error || err.message || "Failed to save finish.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="wb-plain flex w-full items-center gap-1 text-xs font-semibold uppercase tracking-wide text-[var(--text-color)] opacity-70 transition-opacity hover:opacity-100"
      >
        {open ? (
          <ChevronDown className="h-3 w-3" />
        ) : (
          <ChevronRight className="h-3 w-3" />
        )}
        Finishes ({finishRates.length})
      </button>

      {open && (
        <div className="mt-2 flex flex-col gap-1">
          {finishRates.map((finish) => (
            <div key={finish.id}>
              <button
                type="button"
                onClick={() =>
                  editingId === finish.id ? cancel() : startEdit(finish)
                }
                className="wb-plain flex w-full items-center justify-between rounded-md border border-[var(--border-color)] px-2 py-1.5 text-left text-sm hover:bg-[var(--hover-bg-color)]"
              >
                <span className="truncate">{finish.name}</span>
                <span className="shrink-0 text-xs tabular-nums opacity-60">
                  {formatMoney(finish.cost_per_sqft)}/sqft
                </span>
              </button>
              {editingId === finish.id && (
                <FinishEditor
                  draft={draft}
                  setDraft={setDraft}
                  onSave={save}
                  onCancel={cancel}
                  busy={busy}
                  error={error}
                />
              )}
            </div>
          ))}

          {creating ? (
            <FinishEditor
              draft={draft}
              setDraft={setDraft}
              onSave={save}
              onCancel={cancel}
              busy={busy}
              error={error}
            />
          ) : (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="mt-1 justify-center"
              onClick={startCreate}
            >
              <Plus className="h-3 w-3" /> New finish
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

function FinishEditor({ draft, setDraft, onSave, onCancel, busy, error }) {
  const set = (field, value) =>
    setDraft((prev) => ({ ...prev, [field]: value }));
  const derived =
    Number(draft.coverage_sqft_per_gal_per_coat) > 0
      ? (Number(draft.coats) * Number(draft.cost_per_gallon)) /
        Number(draft.coverage_sqft_per_gal_per_coat)
      : 0;
  return (
    <div className="mt-1 space-y-2 rounded-md border border-[var(--border-color)] bg-[color:var(--secondary-color)] p-2">
      <Input
        value={draft.name}
        onChange={(e) => set("name", e.target.value)}
        placeholder="Finish name"
        className="h-8 text-sm"
      />
      <div className="grid grid-cols-3 gap-2">
        <MiniField label="Coats">
          <Input
            type="number"
            min="0"
            step="0.5"
            value={draft.coats}
            onChange={(e) => set("coats", e.target.value)}
            onFocus={(e) => e.target.select()}
            className="h-8 text-right text-xs tabular-nums"
          />
        </MiniField>
        <MiniField label="$/gal">
          <Input
            type="number"
            min="0"
            step="0.01"
            value={draft.cost_per_gallon}
            onChange={(e) => set("cost_per_gallon", e.target.value)}
            onFocus={(e) => e.target.select()}
            className="h-8 text-right text-xs tabular-nums"
          />
        </MiniField>
        <MiniField label="sqft/gal">
          <Input
            type="number"
            min="0"
            step="1"
            value={draft.coverage_sqft_per_gal_per_coat}
            onChange={(e) =>
              set("coverage_sqft_per_gal_per_coat", e.target.value)
            }
            onFocus={(e) => e.target.select()}
            className="h-8 text-right text-xs tabular-nums"
          />
        </MiniField>
      </div>
      <div className="flex items-center justify-between text-xs">
        <span className="opacity-60">Works out to</span>
        <span className="font-semibold tabular-nums">
          {formatMoney(derived)}/sqft
        </span>
      </div>
      {error && (
        <p className="text-xs text-[color:var(--error-color)]">{error}</p>
      )}
      <div className="flex gap-2">
        <Button
          type="button"
          size="sm"
          onClick={onSave}
          disabled={busy}
          className="h-7 flex-1 justify-center"
        >
          {busy ? "Saving…" : "Save"}
        </Button>
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={onCancel}
          disabled={busy}
          className="h-7 justify-center"
        >
          Cancel
        </Button>
      </div>
    </div>
  );
}

function MiniField({ label, children }) {
  return (
    <label className="flex flex-col gap-0.5">
      <span className="text-[10px] uppercase opacity-60">{label}</span>
      {children}
    </label>
  );
}

export default RateCardBuilderPage;
